import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, finalize } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthModel } from '../models/auth.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  private authLocalStorageKey = 'current-user';

  // public fields
  isLoading$: Observable<boolean>;
  isLoadingSubject: BehaviorSubject<boolean>;
  userName$: Observable<string>;
  userNameSubject: BehaviorSubject<string>;

  constructor(private httpClient: HttpClient) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isLoading$ = this.isLoadingSubject.asObservable();

    this.userNameSubject = new BehaviorSubject<string>(this.getUserName());
    this.userName$ = this.userNameSubject.asObservable();
  }

  // public methods
  login(email: string, password: string): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.httpClient
      .post<any>(environment.apiUrl + '/login', { email, password })
      .pipe(
        map((auth: AuthModel) => {
          const result = this.setSession(auth);
          return result;
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  resetPassword(values): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.httpClient
      .post<boolean>(environment.apiUrl + '/ForgetPasswordConfirm', values)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  private setSession(authResult: AuthModel) {
    if (authResult && authResult.token) {
      localStorage.setItem(
        this.authLocalStorageKey,
        JSON.stringify(authResult)
      );
      return true;
    }

    return false;
  }

  logout() {
    localStorage.removeItem(this.authLocalStorageKey);
    document.location.replace('/auth/login');
  }

  public isLoggedIn() {
    const user = JSON.parse(localStorage.getItem(this.authLocalStorageKey));
    return user && moment().isBefore(moment(user.expiration));
  }

  isLoggedOut() {
    return !this.isLoggedIn();
  }

  getUserName() {
    const user = JSON.parse(localStorage.getItem(this.authLocalStorageKey));
    return user && user.displayName;
  }

  getClaims() {
    const user = JSON.parse(localStorage.getItem(this.authLocalStorageKey));
    return user && user.claims;
  }

  showBasedOnClaim(claim) {
    const claims = this.getClaims();
    return claims && claims.length > 0 && claims.includes(claim);
  }

  getToken() {
    const user = JSON.parse(localStorage.getItem(this.authLocalStorageKey));
    return user && user.token;
  }

  refreshToken(): Observable<boolean> {
    return this.httpClient
      .get<any>(environment.apiUrl + '/RefreshToken')
      .pipe(
        map((auth: AuthModel) => {
          const result = this.setSession(auth);
          return result;
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
  }

  setUserName(userName: string) {
    const prevUserStorage = JSON.parse(
      localStorage.getItem(this.authLocalStorageKey)
    );
    this.setSession({ ...prevUserStorage, displayName: userName });
    this.userNameSubject.next(userName);
  }
}
